// DevLandingPage — NEW WIX-style landing page at /dev_lp.
// All sections live: header + drawer, hero, video, "what is", "benefits", CTA, footer.
// Isolated i18n (10 languages) + SEO exclusion (noindex + notranslate). Does NOT touch LandingPage.tsx.

import React, { useEffect, useState } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import devLpI18n from "../../i18n/devlp";
import { DEV_LP_DEFAULT_LANG, getLangDir } from "../../i18n/devlp/languages";
import { useShouldAnimate } from "./useShouldAnimate";
import Reveal from "./components/Reveal";
import DevHeader from "./components/DevHeader";
import HeroSection from "./sections/HeroSection";
import VideoSection from "./sections/VideoSection";
import WhatIsSection from "./sections/WhatIsSection";
import BenefitsSection from "./sections/BenefitsSection";
import CtaSection from "./sections/CtaSection";
import FooterSection from "./sections/FooterSection";

// SEO exclusion: keep /dev_lp out of Google while in development.
// Injects noindex + notranslate meta on mount, removes them on unmount.
const useDevLpHeadGuards = () => {
  useEffect(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex,nofollow";
    robots.setAttribute("data-devlp", "true");
    document.head.appendChild(robots);

    const noTranslate = document.createElement("meta");
    noTranslate.name = "google";
    noTranslate.content = "notranslate";
    noTranslate.setAttribute("data-devlp", "true");
    document.head.appendChild(noTranslate);

    return () => {
      document
        .querySelectorAll('meta[data-devlp="true"]')
        .forEach((el) => el.remove());
    };
  }, []);
};

const DevLandingContent: React.FC = () => {
  const { i18n } = useTranslation();
  const [lang, setLang] = useState<string>(DEV_LP_DEFAULT_LANG);
  const dir = getLangDir(lang);
  const shouldAnimate = useShouldAnimate();

  useDevLpHeadGuards();

  const changeLang = (code: string) => {
    setLang(code);
    i18n.changeLanguage(code);
  };

  return (
    <div
      dir={dir}
      lang={lang}
      translate="no"
      className="notranslate min-h-screen relative overflow-x-hidden"
    >
      {/* gradient background + subtle wave */}
      <div
        className="fixed inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, hsl(201, 60%, 88%), hsl(201, 45%, 97%))",
        }}
        aria-hidden="true"
      >
        {shouldAnimate && (
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z' fill='%23003B7B'%3E%3C/path%3E%3C/svg%3E\")",
              backgroundRepeat: "repeat-x",
              backgroundSize: "1200px 120px",
              animation: "devlp-wave 20s ease-in-out infinite",
            }}
          />
        )}
      </div>

      <DevHeader lang={lang} onLangChange={changeLang} />

      <main>
        <HeroSection />
        <Reveal>
          <VideoSection />
        </Reveal>
        <Reveal>
          <WhatIsSection />
        </Reveal>
        <Reveal>
          <BenefitsSection />
        </Reveal>
        <Reveal>
          <CtaSection />
        </Reveal>
      </main>

      <Reveal>
        <FooterSection />
      </Reveal>

      <style>{`
        @keyframes devlp-wave {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(-50px); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes devlp-rise {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

const DevLandingPage: React.FC = () => {
  return (
    <I18nextProvider i18n={devLpI18n}>
      <DevLandingContent />
    </I18nextProvider>
  );
};

export default DevLandingPage;
